/**
 * Frame sampler vocabulary (ARD-1977).
 *
 * Every episode reports under the single key `perf.app.frame_episode`; the
 * surface and trigger are metadata so a dashboard groups by
 * `@context.metadata.surface` rather than needing one key per surface.
 *
 * The observer is global (one pair of listeners on `document`), so a surface
 * is not a code registration: it is the label `describeFrameTarget` derives
 * from the element that scrolled or transitioned. `FrameSurface` holds the
 * labels the product marks explicitly with `data-frame-surface`, spelled
 * exactly as they reach Datadog (the labeller keeps an authored token as
 * written); anything else reports under its own derived label. Nothing is
 * pinned from this list — the debug strip shows whatever has reported.
 */

/** The marker attribute an element carries to name itself as a surface. */
export const FRAME_SURFACE_ATTRIBUTE = 'data-frame-surface';

export const FrameSurface = {
  SESSION_LIST: 'session_list',
  SESSION_SIDEBAR_LIST: 'session_sidebar_list',
  NOTE_EDITOR: 'note_editor',
  SIDEBAR_PANEL: 'sidebar_panel',
  SESSION_DETAILS_PANEL: 'session_details_panel',
  DOCUMENT: 'document',
} as const;

export type KnownFrameSurface = (typeof FrameSurface)[keyof typeof FrameSurface];

/** Any label: a known constant or one derived from the DOM. */
export type FrameSurface = string;

/**
 * `scroll`: a scroller moved. `transition`: a layout CSS transition ran on the
 * element. `click`: the user clicked; a fixed window after the click measures
 * the re-render it caused (a tab switch, a panel toggle), which no DOM event
 * otherwise reports.
 */
export type FrameTrigger = 'scroll' | 'transition' | 'click';

/** A frame gap above this is one dropped frame at 60 Hz. */
export const LONG_FRAME_MS = 33;

/** A frame gap above this reads as a freeze to the user. */
export const FROZEN_FRAME_MS = 100;

/**
 * The frame budget every episode is normalised against: one frame at 60 Hz.
 *
 * Why a fixed 60 and not the device's own refresh rate. The obvious ratio,
 * `long_frames / frames`, is unusable across a fleet because BOTH ends of it
 * move with the panel: `frames` is the rAF callback count, so a 120 Hz display
 * doubles the denominator, and a fixed 33 ms threshold takes one dropped frame
 * to trip at 60 Hz but three at 120 Hz. The same 100 ms stall in a one-second
 * episode scored 1.8 % on a 60 Hz panel, 1.4 % at 75 Hz and 0.9 % at 120 Hz —
 * a two-to-one spread from hardware alone, in the direction that makes the
 * desktop build (the thing this sampler exists to measure) look worse than a
 * browser on a ProMotion display.
 *
 * Measuring time spent beyond a 60 Hz budget removes the panel from both ends:
 * that same stall is 8.3 % everywhere. 60 is a floor rather than an arbitrary
 * reference — perceived smoothness saturates around it, so a 120 Hz device
 * delivering a steady 60 fps scores 0, which is the honest answer. The cost is
 * deliberate blindness above 60: a ProMotion slide from 120 to 70 fps reports
 * clean. For the freezes this sampler was built for (100 to 400 ms, ARD-1977)
 * that is the right trade; revisit it from `cadence_hz` if it stops being.
 */
export const FRAME_BUDGET_60_MS = 1000 / 60;
